/**
 * Appearance, the same way the AI Workbench does it (webview-ui/src/theme.ts):
 * a HeroUI Pro design system from heroui-theme.css and a colour mode, stamped
 * on <html> as data-theme / data-design-system / the `dark` class so
 * portalled overlays inherit it. The console defaults to "Mojo Up (Solid)"
 * because glass needs an editor behind it to blur.
 */

export type DesignSystem = 'glass' | 'solid';
export type ColorMode = 'auto' | 'light' | 'dark';
export interface Appearance {
  designSystem: DesignSystem;
  colorMode: ColorMode;
}

const STORAGE_KEY = 'aioe.appearance';
const THEME_CLASS: Record<DesignSystem, string> = { glass: 'glass', solid: 'mouve' };
let current: Appearance = { designSystem: 'solid', colorMode: 'auto' };
const listeners = new Set<() => void>();

export function currentAppearance(): Appearance {
  return current;
}

export function subscribeAppearance(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function autoIsLight(): boolean {
  return !window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyAppearance(next: Appearance = current): void {
  if (next.designSystem !== current.designSystem || next.colorMode !== current.colorMode) current = { ...next };
  const light = next.colorMode === 'light' ? true : next.colorMode === 'dark' ? false : autoIsLight();
  const root = document.documentElement;
  root.classList.toggle('dark', !light);
  root.dataset.theme = `${THEME_CLASS[next.designSystem]}-${light ? 'light' : 'dark'}`;
  root.dataset.designSystem = next.designSystem;
  root.style.colorScheme = light ? 'light' : 'dark';
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable */
  }
  for (const l of listeners) l();
}

export function setColorMode(colorMode: ColorMode): void {
  applyAppearance({ ...current, colorMode });
}

export function setDesignSystem(designSystem: DesignSystem): void {
  applyAppearance({ ...current, designSystem });
}

export function installAppearance(): void {
  // `?theme=light|dark` pins a mode for screenshots and reviews without touching the saved preference.
  const pinned = new URLSearchParams(window.location.search).get('theme');
  if (pinned === 'light' || pinned === 'dark') {
    current = { ...current, colorMode: pinned };
    applyAppearance();
    return;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const v = JSON.parse(stored) as Partial<Appearance>;
      if (v.designSystem === 'glass' || v.designSystem === 'solid') current.designSystem = v.designSystem;
      if (v.colorMode === 'auto' || v.colorMode === 'light' || v.colorMode === 'dark') current.colorMode = v.colorMode;
    }
  } catch {
    /* ignore */
  }
  applyAppearance();
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => applyAppearance());
}
